import * as service from "../service";
import type { SponsorTaskRequirement } from "./getSponsors";

export type SponsorTaskAvailabilityConflict = {
  handles: string[];
  sponsor_name?: string | null;
  sponsor_id?: string | null;
  activity_id?: string | null;
};

export type SponsorAgreementTask = {
  id: string;
  title: string;
  type?: string | null;
  requirement: SponsorTaskRequirement;
  available?: boolean;
  unavailable_reason?: string | null;
  unavailable_conflicts?: SponsorTaskAvailabilityConflict[];
};

export type SponsorTaskRequirementsParams = {
  sponsorId: string;
  /** Self-reported participant count for the redemption being signed. */
  participants: number;
  type?: string | null;
  officialIG?: string | null;
  committeeIGs?: string[];
};

/**
 * Resolve the obligation a student is about to sign for, for a declared
 * participant count.
 *
 * The catalog endpoint resolves every task with no participants, which resolves
 * a `follower_count_formula` to its floor; the review route later judges the
 * submission against `sponsor_activity.participants_quantity`. Between those two
 * numbers is the value the signed agreement has to state, so it is fetched from
 * the server rather than computed here.
 *
 * Returns null when the server could not be asked. The caller must NOT fall back
 * to the catalog copy of the task for that case: that copy holds the floor, and
 * a floor printed in a signed contract is exactly the number the student is not
 * held to.
 */
export const GetSponsorTaskRequirementsAPI = async ({
  sponsorId,
  participants,
  type,
  officialIG,
  committeeIGs,
}: SponsorTaskRequirementsParams): Promise<SponsorAgreementTask[] | null> => {
  try {
    const resp = (await service.call("GET", "api/sponsor/taskRequirements", {
      sponsor_id: sponsorId,
      participants,
      type: type || undefined,
      official_ig: officialIG || undefined,
      // Comma-separated, because axios serialises an array as `committee_igs[]`
      // and Express would not read that back under `committee_igs`.
      committee_igs:
        Array.isArray(committeeIGs) && committeeIGs.length > 0
          ? committeeIGs.join(",")
          : undefined,
    })) as { status?: number; result?: any };
    if (resp?.status !== 0 || !Array.isArray(resp.result?.tasks)) {
      console.error(`GetSponsorTaskRequirementsAPI ${JSON.stringify(resp?.status)}`);
      return null;
    }
    return resp.result.tasks.map((task: any, index: number) => ({
      id: String(task?.id ?? index),
      title: String(task?.title ?? ""),
      type: task?.type ?? null,
      requirement: (task?.requirement ?? null) as SponsorTaskRequirement,
      available: task?.available !== false,
      unavailable_reason: task?.unavailable_reason ?? null,
      unavailable_conflicts: Array.isArray(task?.unavailable_conflicts)
        ? task.unavailable_conflicts
        : [],
    }));
  } catch (err) {
    console.error(`GetSponsorTaskRequirementsAPI ${err}`);
    return null;
  }
};